import ApexCharts, { ApexOptions } from "apexcharts";
import React, { useEffect, useRef } from "react";
import { getCSS } from "../../../assets/ts/_utils";
import { useThemeMode } from "../../layout/theme-mode/ThemeModeProvider";

type Props = {
  className: string;
  labels: string[];
  dataList: number[];
  title: string;
  eyebrow?: string;
  description?: string;
};

const EventsPerMonthChart: React.FC<Props> = ({
  className,
  labels,
  dataList,
  title,
  eyebrow,
  description,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const { mode } = useThemeMode();
  const hasData = dataList.some((value) => value > 0);

  useEffect(() => {
    if (!chartRef.current || !labels.length || !dataList.length || !hasData) {
      return;
    }

    const height = parseInt(getCSS(chartRef.current, "height"));
    const chart = new ApexCharts(
      chartRef.current,
      getBarChartOptions(height, labels, dataList),
    );
    chart.render();

    return () => {
      chart.destroy();
    };
  }, [chartRef, mode, labels, dataList, hasData]);

  return (
    <div className={`card dashboard-chart-card ${className}`}>
      <div className="card-header border-0 pb-0">
        <div className="dashboard-chart-card__heading">
          {eyebrow ? (
            <span className="dashboard-chart-card__eyebrow">{eyebrow}</span>
          ) : null}
          <h3 className="dashboard-chart-card__title">{title}</h3>
          <p className="dashboard-chart-card__description">
            {description ?? "Monthly event creation overview"}
          </p>
        </div>
      </div>
      <div className="card-body dashboard-chart-card__body">
        {!hasData && (
          <div className="dashboard-chart-empty">
            <span className="dashboard-chart-empty__icon">EP</span>
            <span className="dashboard-chart-empty__copy">
              <span className="dashboard-chart-empty__title">
                No events created yet
              </span>
              <span className="dashboard-chart-empty__subtitle">
                Event publishing activity will appear here over time.
              </span>
            </span>
          </div>
        )}
        <div
          ref={chartRef}
          className="dashboard-chart-canvas"
          style={{ height: "350px", opacity: hasData ? 1 : 0.22 }}
        ></div>
      </div>
    </div>
  );
};

export { EventsPerMonthChart };

function getBarChartOptions(
  height: number,
  labels: string[],
  dataList: number[],
): ApexOptions {
  return {
    series: [{ name: "Events", data: dataList }],
    chart: {
      fontFamily: "inherit",
      type: "bar",
      height,
      toolbar: { show: false },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
        animateGradually: { enabled: true, delay: 100 },
        dynamicAnimation: { enabled: true, speed: 350 },
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "44%",
        borderRadius: 12,
        dataLabels: {
          position: "top",
        },
      },
    },
    colors: ["#EA580C"],
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.18,
        gradientToColors: ["#FBBF24"],
        inverseColors: false,
        opacityFrom: 0.97,
        opacityTo: 0.82,
        stops: [0, 100],
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        return val > 0 ? val.toString() : "";
      },
      offsetY: -14,
      style: { fontSize: "12px", fontWeight: 800, colors: ["#0F172A"] },
      background: {
        enabled: false,
        dropShadow: {
          enabled: false,
        },
      },
      dropShadow: {
        enabled: false,
      },
    },
    legend: { show: false },
    stroke: { show: true, width: 0, colors: ["transparent"] },
    xaxis: {
      categories: labels,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        rotate: 0,
        style: { colors: "#64748B", fontSize: "12px", fontWeight: 600 },
      },
    },
    yaxis: {
      forceNiceScale: true,
      labels: {
        style: { colors: "#94A3B8", fontSize: "12px", fontWeight: 500 },
        formatter: function (val: number) {
          return Math.round(val).toString();
        },
      },
    },
    grid: {
      borderColor: "#E2E8F0",
      strokeDashArray: 5,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 18, right: 8, bottom: 0, left: 8 },
    },
    tooltip: {
      enabled: true,
      theme: "light",
      style: { fontSize: "13px" },
      y: {
        formatter: function (val: number) {
          return val + " event(s)";
        },
      },
      marker: {
        show: false,
      },
    },
    states: {
      hover: {
        filter: { type: "lighten", value: 0.04 },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: { type: "lighten", value: 0.02 },
      },
    },
  };
}
